import React, { useEffect, useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { api } from '@/lib/api';
import { KeyRound } from 'lucide-react';
import { useNoIndex } from '@/hooks/useSEO';

export default function ResetPassword() {
    useNoIndex('Set new password — Linguistic Quest');
    const [params] = useSearchParams();
    const nav = useNavigate();
    const token = params.get('token') || '';
    const [pw, setPw] = useState('');
    const [pw2, setPw2] = useState('');
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState(null);
    const [done, setDone] = useState(false);

    useEffect(() => {
        if (!token) setErr('Missing reset token. Please use the link from your email.');
    }, [token]);

    const submit = async (e) => {
        e.preventDefault();
        if (pw.length < 8) return setErr('Password must be at least 8 characters.');
        if (pw !== pw2) return setErr('The two passwords do not match.');
        setBusy(true); setErr(null);
        try {
            await api.post('/auth/reset-password', { token, new_password: pw });
            setDone(true);
            setTimeout(() => nav('/login'), 2500);
        } catch (e) {
            setErr(e?.response?.data?.detail || 'Could not reset password. The link may have expired — request a fresh one.');
        } finally { setBusy(false); }
    };

    return (
        <main className="max-w-md mx-auto px-6 py-16" data-testid="page-reset-password">
            <div className="flex justify-center"><KeyRound size={32} className="text-lq-purple-500"/></div>
            <h1 className="mt-4 font-display text-3xl font-semibold text-lq-ink-black text-center">Choose a new password</h1>

            {done ? (
                <div className="mt-8 bg-lq-peach-100 border border-lq-peach-300 rounded-2xl p-5 text-center" data-testid="reset-password-done">
                    <p className="text-lq-ink-900">Password updated. Redirecting you to sign in…</p>
                </div>
            ) : (
                <form onSubmit={submit} className="mt-8 space-y-4" data-testid="reset-password-form">
                    <label className="block">
                        <span className="text-sm font-medium text-lq-ink-900">New password</span>
                        <input type="password" required minLength={8} value={pw} onChange={(e) => setPw(e.target.value)}
                            className="mt-1 w-full px-4 py-3 rounded-xl border border-lq-ink-300 bg-white focus:border-lq-purple-500 outline-none"
                            data-testid="reset-password-new" />
                    </label>
                    <label className="block">
                        <span className="text-sm font-medium text-lq-ink-900">Confirm new password</span>
                        <input type="password" required minLength={8} value={pw2} onChange={(e) => setPw2(e.target.value)}
                            className="mt-1 w-full px-4 py-3 rounded-xl border border-lq-ink-300 bg-white focus:border-lq-purple-500 outline-none"
                            data-testid="reset-password-confirm" />
                    </label>
                    {err && <p className="text-sm text-lq-error">{err}</p>}
                    <button type="submit" disabled={busy || !token} className="w-full py-3 rounded-full bg-lq-purple-500 hover:bg-lq-purple-700 text-white font-semibold disabled:opacity-60" data-testid="reset-password-submit">
                        {busy ? 'Saving…' : 'Save new password'}
                    </button>
                    <p className="text-sm text-center text-lq-ink-500">
                        <Link to="/forgot-password" className="text-lq-purple-700 hover:underline">Need a fresh link?</Link>
                    </p>
                </form>
            )}
        </main>
    );
}
